import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { BookCollectionService, BookCollection } from '../services/bookCollectionService';
import toast from 'react-hot-toast';

interface CloneCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collection: Pick<BookCollection, 'collection_id' | '_id' | 'title'>;
  onSuccess: (newCollection: BookCollection) => void;
}

export const defaultCloneTitle = (title: string) => `${title} (bản sao)`;

const CloneCollectionDialog: React.FC<CloneCollectionDialogProps> = ({
  open,
  onOpenChange,
  collection,
  onSuccess,
}) => {
  const [title, setTitle] = useState(defaultCloneTitle(collection.title));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(defaultCloneTitle(collection.title));
      setError('');
    }
  }, [open, collection.title]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Tên bộ sách mới là bắt buộc');
      return;
    }

    setLoading(true);
    try {
      const response = await BookCollectionService.cloneCollection(
        collection.collection_id || collection._id!,
        title.trim()
      );
      toast.success('Đã nhân bản bộ sách');
      onOpenChange(false);
      onSuccess(response.data);
    } catch (err: any) {
      console.error('Clone collection failed:', err);
      toast.error(`Lỗi nhân bản: ${err?.response?.data?.message || err?.message || 'Có lỗi xảy ra'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nhân bản bộ sách</DialogTitle>
          <DialogDescription>
            Bộ gốc: <strong>{collection.title}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg space-y-1">
            <p className="text-xs text-blue-700">
              Mỗi sách trong bộ được nhân bản thành sách mới (ở trạng thái nháp), giữ nguyên thứ tự, nội dung và
              cấu hình bật/tắt chương, tiết. Chỉnh sửa bộ mới không ảnh hưởng bộ gốc.
            </p>
            <p className="text-xs text-blue-700">
              Bộ mới ở trạng thái nháp và không mang theo mã kích hoạt của bộ gốc.
            </p>
          </div>

          <div>
            <label htmlFor="clone-collection-title" className="block text-sm font-medium text-gray-700 mb-1">
              Tên bộ sách mới <span className="text-red-500">*</span>
            </label>
            <Input
              id="clone-collection-title"
              value={title}
              maxLength={255}
              autoFocus
              disabled={loading}
              onChange={(e) => {
                setTitle(e.target.value);
                if (error) setError('');
              }}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Đang nhân bản...
                </span>
              ) : (
                'Nhân bản'
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CloneCollectionDialog;
